import type { OverlayRef } from "@angular/cdk/overlay";
import { Component, DestroyRef, effect, inject, input, type OnDestroy, output } from "@angular/core";
import { AlertService } from "@ui";
import { Observable, type Subscription } from "rxjs";
import { finalize, switchMap, tap } from "rxjs/operators";
import { type ColumnTypes, DataGridComponent } from "../../data-grid/data-grid.component";
import type { ColumnMeta, Corpus } from "@pienso/ingest-types/corpus.interface";
import { isFinished, isTransform, type StatusResponse, TaskType } from "@pienso/ingest-types/task.interface";
import { ColumnTypesTransformDetails, CorpusDataService } from "../../services/corpus-data.service";
import { TaskDataService } from "../../services/task-data.service";
import { ToolbarComponent } from "../../transform/toolbar.component";
import { TransformerGridDataService } from "../../transform/transformer-grid-data.service";
import { TransformerGridRendererService } from "../../transform/transformer-grid-renderer.service";
import { TransformHistoryComponent } from "../../transform-history/transform-history.component";

// Refine view for the ingest modal: replaces the old routed RefineComponent.
@Component({
  selector: "app-ingest-modal-refine",
  templateUrl: "./refine.component.html",
  styleUrls: ["./refine.component.scss"],
  imports: [ToolbarComponent, DataGridComponent, TransformHistoryComponent],
})
export class IngestModalRefineComponent implements OnDestroy {
  private corpusService = inject(CorpusDataService);
  gridDataService = inject(TransformerGridDataService);
  gridRendererService = inject(TransformerGridRendererService);
  private taskService = inject(TaskDataService);
  private alertService = inject(AlertService).withDestroyRef(inject(DestroyRef));
  readonly corpusId = input.required<number>();

  // emitted when a copy is derived, so the parent can track the new corpus id
  readonly corpusIdChange = output<number>();
  // emitted when the user is ready to move to the format step
  readonly formatRequested = output<number>();
  // keeps the parent modal header synchronized with the loaded corpus
  readonly corpusChange = output<Corpus>();
  // emitted when the user wants to return to the previous ingest modal step
  readonly back = output<void>();

  corpus: Corpus | undefined; // note that the corpus object can change while the corpusId stays constant

  isDeriving = false;
  isDownloading = false;
  isTransforming = false;

  editable = true;

  private readOnlyAlertRef: OverlayRef | undefined;

  private typeChangeSubscription: Subscription | null = null;

  private taskSubscription: Subscription | null = null;

  constructor() {
    // reload whenever the corpus id (input) changes, e.g. after deriving a copy
    effect((onCleanup) => {
      const corpusId = this.corpusId();

      this.taskSubscription = this.taskService
        .watchEntityTasks(corpusId, [
          TaskType.CORPUS_DERIVE,
          TaskType.CORPUS_DOWNLOAD,
          TaskType.TRANSFORM_CREATE,
          TaskType.TRANSFORM_UPDATE,
          TaskType.TRANSFORM_REMOVE,
        ])
        .subscribe((responses: StatusResponse[]) => {
          this.isDeriving = responses.some((sr) => sr.type === TaskType.CORPUS_DERIVE && !isFinished(sr));
          this.isDownloading = responses.some((sr) => sr.type === TaskType.CORPUS_DOWNLOAD && !isFinished(sr));
          const isTransforming = responses.some((sr) => isTransform(sr) && !isFinished(sr));
          const finishedTransform = responses.some((sr) => isTransform(sr) && isFinished(sr));
          const isRemoval = responses.find((sr) => sr.type === TaskType.TRANSFORM_REMOVE);

          // If it's a removal, we immediately reload the corpus with the response details to avoid the data service
          // from fetching a deleted esIndex
          if (isRemoval && finishedTransform) {
            this.loadFromCorpus(
              new Observable<Corpus>((subscriber) => {
                subscriber.next(isRemoval.details);
                subscriber.complete();
              }),
            );
          } else if (finishedTransform || (this.isTransforming && !isTransforming)) {
            // reload the corpus
            this.loadFromCorpus(this.corpusService.getById(corpusId));
          }
          this.isTransforming = isTransforming;
        });
      onCleanup(() => this.taskSubscription?.unsubscribe());

      this.loadFromCorpus(this.corpusService.getById(corpusId));
    });
  }

  ngOnDestroy() {
    if (this.typeChangeSubscription) {
      this.typeChangeSubscription.unsubscribe();
    }
  }

  private loadFromCorpus(obs: Observable<Corpus>) {
    return obs.subscribe({
      next: (corpus: Corpus) => {
        this.corpus = corpus;
        this.corpusChange.emit(corpus);
        this.gridDataService.setCorpus(corpus);
        this.editable = corpus && (!corpus.pifs || !corpus.pifs.length);
        this.updateReadOnlyAlert();
      },
      error: (e) => this.alertService.error(e),
    });
  }

  columnsChanged($event: ColumnMeta) {
    const obs = this.corpusService.updateColumnMetadata(this.corpusId(), $event);
    this.loadFromCorpus(obs);
  }

  private updateReadOnlyAlert() {
    if (this.corpus && !this.editable) {
      if (!this.readOnlyAlertRef) {
        this.readOnlyAlertRef = this.alertService.warn(
          "This dataset is read-only because it\u2019s used by training datasets. Save a copy to edit it.",
          {
            type: "banner",
            showClose: false,
            duration: 0,
            action: { text: "Save a copy", callback: () => this.deriveCorpus() },
          },
        );
      }
    } else {
      this.readOnlyAlertRef?.dispose();
      this.readOnlyAlertRef = undefined;
    }
  }

  columnTypeChanged($event: ColumnTypes) {
    const transformDetails = new ColumnTypesTransformDetails($event);
    if (this.typeChangeSubscription) {
      this.typeChangeSubscription.unsubscribe();
    }
    const obs = this.corpusService.transformCorpus(this.corpusId(), transformDetails).pipe(
      switchMap(() => this.corpusService.getById(this.corpusId())),
      tap(() => {
        this.typeChangeSubscription = null;
      }),
    );
    this.typeChangeSubscription = this.loadFromCorpus(obs);
  }

  nameChanged(name: string) {
    if (!this.corpus) {
      return;
    }
    if (name !== this.corpus.corpusName) {
      this.corpus.corpusName = name;
      const obs = this.corpusService.updateCorpus(this.corpus);
      this.loadFromCorpus(obs);
    }
  }

  deriveCorpus() {
    this.isDeriving = true;
    this.corpusService
      .deriveCorpus(this.corpusId())
      .pipe(finalize(() => (this.isDeriving = false)))
      .subscribe({
        next: (corpusId: number) => {
          this.alertService.success("You are now editing a copy of the previous data set.");
          this.corpusIdChange.emit(corpusId);
        },
        error: (e) => this.alertService.error(e),
      });
  }

  downloadCorpusData() {
    this.isDownloading = true;
    this.corpusService
      .downloadCorpus(this.corpus!)
      .pipe(finalize(() => (this.isDownloading = false)))
      .subscribe({ error: (e) => this.alertService.error(e) });
  }

  createTrainingData() {
    this.formatRequested.emit(this.corpus!.id);
  }
}
